import {
  usePricing,
  useProject,
} from '@customink/project-service-react-components';
import { SHIPPING_TYPES } from '@customink/project-service-types';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { hasValidZipCodeAtom } from './useValidateZipCode';

import { ORDER_TYPES } from '@/constants';
import { isProjectSettledAtom } from '@/hooks/Project/useIsProjectSettled';
import { useOrderQuantityType } from '@/hooks/useOrderQuantityType';
import { fireEvent, pageViewTag } from '@/utils/metrics';

export function useAppMetrics() {
  const { data: project } = useProject();
  const isProjectSettled = useAtomValue(isProjectSettledAtom);
  const isCartEmpty = !project?.items || project.items.length === 0;
  const quantityType = useOrderQuantityType();
  const hasValidZipCode = useAtomValue(hasValidZipCodeAtom);
  const hasReorder: boolean = quantityType === ORDER_TYPES.REORDER;
  const { isInvalid: invalidPrice } = usePricing();

  useEffect(() => {
    if (!isProjectSettled) return;

    if (isCartEmpty) {
      pageViewTag('checkout/summary/empty_cart');
    } else {
      pageViewTag('checkout/summary/save/confirm');
    }
  }, [isProjectSettled, isCartEmpty]);

  useEffect(() => {
    if (!isProjectSettled || !project?.items || invalidPrice) return;

    hasReorder && pageViewTag('checkout/summary/reorder');
  }, [isProjectSettled, project?.items, hasReorder, invalidPrice]);

  useEffect(() => {
    if (!isProjectSettled || isCartEmpty || invalidPrice) return;

    pageViewTag(`checkout/summary/${quantityType}`);
  }, [isProjectSettled, isCartEmpty, quantityType, invalidPrice]);

  useEffect(() => {
    if (!isProjectSettled || !project?.items) return;

    const missingQuantity = project.items.some(
      (item) => item.totalQuantity === 0,
    );

    if (missingQuantity && hasValidZipCode) {
      pageViewTag(`checkout/summary/${quantityType}/wcibmo/enter_quantities`);
    }
  }, [hasValidZipCode, isProjectSettled, project?.items, quantityType]);

  useEffect(() => {
    const shippingType = project?.configuration?.shippingType;
    if (!isProjectSettled || !shippingType) return;

    let eventAction = shippingType.toLowerCase();
    if (shippingType === SHIPPING_TYPES.INDIVIDUAL) {
      eventAction = 'bulk split ship';
    }

    fireEvent('checkout delivery options', eventAction, 'cart');
  }, [isProjectSettled, project?.configuration?.shippingType]);
}
